import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import {
  Box,
  Typography,
  Button,
  useTheme,
  IconButton,
  Tooltip,
  TextField,
  CircularProgress,
} from "@mui/material";
import {
  PamGrid,
  GridConfigMenu,
  useGridConfigs,
  ConfirmationDialog,
  HistoryModal,
  parseFileData,
  mergeRows,
  createColumnNormalizer,
  normalizeRowKeys,
  buildHeaderMappingsFromAliases,
  isPermissionDenied,
  getPermissionDeniedText,
  RequestAccessButton,
  getPermissionDeniedMessage,
  usePermission,
  withLoadedModifiedDate,
  getWriteErrorMessage,
  shouldReloadAfterWriteError,
  isVersionConflict,
  isOutcomeUnknown,
} from "@1823-partners/core";
import {
  CellStyleModule,
  ClientSideRowModelModule,
  DateFilterModule,
  NumberFilterModule,
  QuickFilterModule,
  TextFilterModule,
} from "ag-grid-community";
import {
  ExcelExportModule,
} from "ag-grid-enterprise";
import {
  Refresh as RefreshIcon,
  Search as SearchIcon,
  Download as DownloadIcon,
  Visibility as ViewIcon,
  CalendarToday as CalendarIcon,
  PlayArrow as PlayIcon,
  History as HistoryIcon,
  Save as SaveIcon,
  Upload as UploadIcon,
  MenuBook as MenuBookIcon
} from "@mui/icons-material";
import api, { getSurveillance, runSurveillance, runBook, getSurveillanceRowHistory, saveSurveillance } from "../api";
import SurveillanceHistory from "./SurveillanceHistory";
import SurveillanceDetails from "./SurveillanceDetails";
import RunBookModal from "./RunBookModal";

// Static alias map: display/alternative names → database field names.
// Used with buildHeaderMappingsFromAliases + createColumnNormalizer for upload column matching.
const SURVEILLANCE_FIELD_ALIASES = {
  SURVEILLANCE_ID: ['Surveillance ID', 'SURVEILLANCE_ID'],
  CUSIP: ['CUSIP', 'cusip', 'Cusip'],
  SECURITY: ['Security', 'SECURITY'],
  NAIC_DESIGNATIONS: ['NAIC Designations', 'NAIC_DESIGNATIONS', 'NAIC'],
  BROAD_ASSET_CLASS: ['Broad Asset Class', 'BROAD_ASSET_CLASS'],
  FINAL_GROUPING: ['Final Grouping', 'FINAL_GROUPING'],
  BOOKVALUE: ['Book Value', 'BOOKVALUE', 'Book_Value'],
  GAINLOSS: ['Gain/Loss', 'GAINLOSS', 'Gain Loss'],
  CARRYVALUE: ['Carry Value', 'CARRYVALUE', 'Carry_Value'],
  MARKETPRICE: ['Market Price', 'MARKETPRICE', 'Market_Price'],
  WAL_F: ['WAL F', 'WAL_F', 'WAL'],
  YLD_F: ['YLD F', 'YLD_F', 'Yield', 'YLD'],
  SPREAD: ['Spread', 'SPREAD'],
  WARF: ['WARF', 'warf'],
  CE: ['CE', 'ce'],
  THICK: ['Thickness', 'THICK', 'Thick'],
  CPR1M: ['CPR 1M', 'CPR1M'],
  CPR3M: ['CPR 3M', 'CPR3M'],
  CPR6M: ['CPR 6M', 'CPR6M'],
  CPR12M: ['CPR 12M', 'CPR12M'],
  CDR1M: ['CDR 1M', 'CDR1M'],
  CDR3M: ['CDR 3M', 'CDR3M'],
  CDR6M: ['CDR 6M', 'CDR6M'],
  CDR12M: ['CDR 12M', 'CDR12M'],
  SEV1M: ['SEV 1M', 'SEV1M'],
  SEV3M: ['SEV 3M', 'SEV3M'],
  SEV6M: ['SEV 6M', 'SEV6M'],
  SEV12M: ['SEV 12M', 'SEV12M'],
  DQ30P: ['DQ30+', 'DQ30P', 'DQ30'],
  DQ60P: ['DQ60+', 'DQ60P', 'DQ60'],
  LTV: ['LTV', 'ltv'],
  LEVERAGE: ['Leverage', 'LEVERAGE'],
  OCCUPANCY: ['Occupancy', 'OCCUPANCY'],
  WAC: ['WAC', 'wac'],
  RTG_SP: ["S&P Rating", 'RTG_SP', 'SP Rating'],
  RTG_MOODY: ["Moody's Rating", 'RTG_MOODY', 'Moodys Rating'],
  RTG_FITCH: ['Fitch Rating', 'RTG_FITCH'],
  GROUP_W_RATING: ['Group W Rating', 'GROUP_W_RATING'],
  RISK_RATING: ['Risk Rating', 'RISK_RATING'],
  CLASS_RATING: ['Class Rating', 'CLASS_RATING'],
  SETTLE_DT: ['Settle Date', 'SETTLE_DT'],
  ASOFDT: ['As Of Date', 'ASOFDT', 'As_Of_Date'],
  CDU_DATE: ['CDU Date', 'CDU_DATE'],
  DEAL_NAME: ['Deal Name', 'DEAL_NAME'],
  SECURITY_NAME_SOURCE: ['Security Name Source', 'SECURITY_NAME_SOURCE'],
  SPECSERV: ['Special Servicing', 'SPECSERV'],
  DEBTSERV: ['Debt Service', 'DEBTSERV'],
  NOIDY: ['NOI/DY', 'NOIDY'],
  UTILIZATION: ['Utilization', 'UTILIZATION'],
  MVOC: ['MVOC', 'mvoc'],
  STR_PCT: ['STR Pct', 'STR_PCT'],
  CCC: ['CCC', 'ccc'],
  CDO_WARF: ['CDO WARF', 'CDO_WARF'],
  SSSGT: ['SSSGT', 'sssgt'],
  AAA_SPREAD: ['AAA Spread', 'AAA_SPREAD'],
  CDO_MP: ['CDO MP', 'CDO_MP'],
  ACCUMLOSS: ['Accumulated Loss', 'ACCUMLOSS'],
  RTG_KBRA: ['KBRA Rating', 'RTG_KBRA'],
  RTG_DBRS: ['DBRS Rating', 'RTG_DBRS'],
  RTG_BSCR: ['BSCR Rating', 'RTG_BSCR'],
  RTG_EJ: ['EJ Rating', 'RTG_EJ'],
  SETTLE_DATE: ['Settle Date Alt', 'SETTLE_DATE'],
  CALLABLE: ['Callable', 'CALLABLE'],
  INTEXSTATUS: ['Status', 'INTEXSTATUS'],
  LOADID: ['Load ID', 'LOADID'],
  COLLAT_MANAGER: ['Collateral Manager', 'COLLAT_MANAGER'],
  SPONSOR_MANAGER: ['Sponsor Manager', 'SPONSOR_MANAGER'],
  ISSUER_TRUST: ['Issuer Trust', 'ISSUER_TRUST'],
  PROSPERITY_ISSUER_CONCENTRATION: ['Issuer Concentration', 'PROSPERITY_ISSUER_CONCENTRATION'],
  NOTES: ['Notes', 'NOTES'],
  BOOKPRICE: ['Book Price', 'BOOKPRICE', 'Book_Price'],
  MODIFIED_BY: ['Modified By', 'MODIFIED_BY'],
  MODIFIED_DATE: ['Modified Date', 'MODIFIED_DATE'],
};

// Column normalizer: maps raw header names (any case/separator) to database field names.
const surveillanceColumnNormalizer = createColumnNormalizer(
  buildHeaderMappingsFromAliases(SURVEILLANCE_FIELD_ALIASES)
);

// Date fields that need special YYYY-MM-DD formatting during merge.
const DATE_FIELDS = new Set(['ASOFDT', 'CDU_DATE', 'SETTLE_DT', 'SETTLE_DATE']);

// System fields that should never be treated as changed-by-upload.
const SYSTEM_FIELDS = new Set([
  'MODIFIED_BY', 'MODIFIED_DATE', 'CUSIP', 'SURVEILLANCE_ID',
  // These fields define which pipeline run / date a row belongs to. Changing
  // one would move an edited row out of the grid, or duplicate it in another.
  'SETTLE_DATE', 'SETTLE_DT', 'LOADID', 'RUN_TYPE', 'BOOKS',
]);

// Stable row id: derive directly from the row data (PamGrid wraps this as
// (params) => getRowId(params.data) internally). Must stay SURVEILLANCE_ID —
// save/forEachNode/change-tracking all key off it.
const getSurveillanceRowId = (data) => data.SURVEILLANCE_ID;

/**
 * Normalize a date value to YYYY-MM-DD string.
 * Returns the original value if it cannot be parsed.
 */
function normalizeDateValue(value) {
  if (value === null || value === undefined || value === '') return value;
  if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(value)) return value;
  if (value instanceof Date) {
    const y = value.getUTCFullYear();
    const m = String(value.getUTCMonth() + 1).padStart(2, '0');
    const d = String(value.getUTCDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
  }
  if (typeof value === 'string' && value.includes('T')) return value.split('T')[0];
  try {
    const date = new Date(value);
    if (!isNaN(date.getTime())) {
      const y = date.getUTCFullYear();
      const m = String(date.getUTCMonth() + 1).padStart(2, '0');
      const d = String(date.getUTCDate()).padStart(2, '0');
      return `${y}-${m}-${d}`;
    }
  } catch (_) {
    // fall through
  }
  return value;
}

// Puts a failed save's edits back onto reloaded rows. Edits to a row the new
// load no longer contains are returned separately (`orphanedEdits`): they are
// shown to the user outside the grid, never mixed into the live rows (where
// they could be exported as current positions) and never saved.
export function reapplyPendingEdits(freshRows, pendingEdits) {
  const changedCells = new Map();
  if (!pendingEdits || pendingEdits.size === 0) {
    return { rows: freshRows, changedCells, orphanedEdits: [] };
  }
  const normalize = (v) => (v === null || v === undefined || v === '' ? null : String(v).trim());
  const rows = freshRows.map((row) => {
    const pending = pendingEdits.get(row.SURVEILLANCE_ID);
    if (!pending) return row;
    const merged = { ...row };
    const fields = new Set();
    Object.entries(pending.values).forEach(([field, value]) => {
      if (normalize(row[field]) === normalize(value)) return;
      merged[field] = value;
      fields.add(field);
    });
    if (fields.size > 0) changedCells.set(row.SURVEILLANCE_ID, fields);
    return merged;
  });
  const freshIds = new Set(freshRows.map(fresh => fresh.SURVEILLANCE_ID));
  const orphanedEdits = [...pendingEdits.values()]
    .filter(({ row }) => !freshIds.has(row.SURVEILLANCE_ID))
    .map(({ row, values }) => ({
      SURVEILLANCE_ID: row.SURVEILLANCE_ID,
      CUSIP: row.CUSIP,
      // Where the edit came from, so it is never read as belonging to
      // another date or load.
      SETTLE_DATE: row.SETTLE_DATE,
      LOADID: row.LOADID,
      values: { ...values },
    }));
  return { rows, changedCells, orphanedEdits };
}

// Adds newly vanished-row edits to the ones already listed for `sourceDate`.
// A row listed twice keeps its earlier fields plus the later values.
export function mergeOrphanedEdits(previous, sourceDate, newEdits) {
  const kept = previous && previous.sourceDate === sourceDate ? previous.edits : [];
  const byId = new Map(kept.map(edit => [edit.SURVEILLANCE_ID, edit]));
  newEdits.forEach(edit => {
    const earlier = byId.get(edit.SURVEILLANCE_ID);
    byId.set(edit.SURVEILLANCE_ID, earlier
      ? { ...earlier, ...edit, values: { ...earlier.values, ...edit.values } }
      : edit);
  });
  return { sourceDate, edits: [...byId.values()] };
}

// Shown when a run request got no confirmed answer (it may still have queued).
export const RUN_OUTCOME_UNKNOWN_MESSAGE =
  'The run request was not confirmed, so it may or may not have been queued. Check Run History before requesting it again.';

// Run props (owned by Dashboard's run monitor):
// - isRunning / isBookRunning: a Monthly / Book run for selectedDate is
//   REQUESTED or IN PROGRESS, so its Run button stays disabled.
// - onRunRequested(payload): a 202 from /run-surveillance or /run-book.
// - onRunAlreadyActive(run): a 409 naming the run already queued/running.
// - onRunOutcomeUnknown(): the request may or may not have been queued.
// - completedRun: the run that most recently finished (refresh its date).
const SurveillanceGrid = ({
  userId,
  isRunning = false,
  isBookRunning = false,
  onRunRequested,
  onRunAlreadyActive,
  onRunOutcomeUnknown,
  completedRun = null,
  selectedDate,
  onUnsavedChangesChange,
}) => {
  // UI hints from the shell's permission center; the server enforces both gates.
  const canPerformActions = usePermission('surveillance.books:run');
  const canSaveResults = usePermission('surveillance.results:write');
  const [quickFilterText, setQuickFilterText] = useState('');
  const [rowData, setRowData] = useState([]);
  const [error, setError] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  // True while a load that will replace the rows and their change tracking is
  // in flight (a date switch, Refresh, a post-save reload). Editing and Save
  // wait for it; an edit made meanwhile would be lost. A finished-run reload
  // (`unlessEdited`) is not one: it backs off if the user edits meanwhile.
  const [isReplacingRows, setIsReplacingRows] = useState(false);
  const [message, setMessage] = useState(null);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [showHistoryModal, setShowHistoryModal] = useState(false);
  const [historyData, setHistoryData] = useState([]);
  const [historyTitle, setHistoryTitle] = useState('');
  const [isLoadingHistory, setIsLoadingHistory] = useState(false);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [detailRowData, setDetailRowData] = useState(null);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  // Mirrors hasUnsavedChanges for async code. Edits also set it directly, so a
  // response that lands before the next render still sees them.
  const hasUnsavedChangesRef = useRef(false);
  hasUnsavedChangesRef.current = hasUnsavedChanges;
  const [modifiedRows, setModifiedRows] = useState(new Set());
  const [changedCells, setChangedCells] = useState(new Map()); // Map<rowId, Set<fieldName>>
  const [originalRowData, setOriginalRowData] = useState(new Map()); // Map<rowId, originalRowData>
  const [isSaving, setIsSaving] = useState(false);
  // Edits whose row vanished from the reloaded run; shown, never saved.
  // { sourceDate, edits } — scoped to the date they were made on.
  const [orphaned, setOrphaned] = useState(null);
  const orphanedEdits = (orphaned && orphaned.sourceDate === selectedDate) ? orphaned.edits : [];
  const [isUploading, setIsUploading] = useState(false);
  const [showRunBookModal, setShowRunBookModal] = useState(false);
  // True only while the POST that requests a run is in flight.
  const [isRequestingRun, setIsRequestingRun] = useState(false);
  const [isRequestingBook, setIsRequestingBook] = useState(false);
  const theme = useTheme();
  const gridRef = useRef(null);
  const fileInputRef = useRef(null);
  // The date the rows on screen were loaded for. Until rows for selectedDate
  // have landed, the grid is read-only and Save/Upload/Export are disabled, so
  // nothing can be written or exported under the wrong date.
  const [loadedDate, setLoadedDate] = useState(null);
  const rowsMatchSelectedDate = Boolean(selectedDate) && loadedDate === selectedDate;
  // The date on screen now, for async code that started under another one.
  const selectedDateRef = useRef(selectedDate);
  selectedDateRef.current = selectedDate;
  // Every load takes a generation; a response whose generation is no longer
  // current (the date changed or a newer load started) is dropped.
  const loadGenerationRef = useRef(0);

  // A response that lands after unmount must not touch state.
  useEffect(() => () => {
    loadGenerationRef.current += 1;
  }, []);

  // Tell the Dashboard whether switching dates would discard edits.
  useEffect(() => {
    onUnsavedChangesChange?.(hasUnsavedChanges);
  }, [hasUnsavedChanges, onUnsavedChangesChange]);
  useEffect(() => () => onUnsavedChangesChange?.(false), [onUnsavedChangesChange]);

  // Per-user view-options (column order/width/visibility/sort/filter) backed
  // by the shared /grid-configs blueprint. Owning the hook here lets us bridge
  // its onGridReady into PamGrid so a default layout auto-applies once ready.
  // A /grid-configs failure is caught inside the hook and never breaks the grid.
  const gridConfigs = useGridConfigs(api, {
    app: 'surveillance',
    gridId: 'surveillance-positions',
    gridApiRef: gridRef,
  });

  // `unlessEdited`: a background refresh (a run finished). If the user has
  // unsaved edits by the time the rows arrive, they are not applied: that
  // would discard the edits.
  const loadSurveillanceData = useCallback(async (dateToLoad, pendingEdits = null, { unlessEdited = false } = {}) => {
    if (!dateToLoad) {
      return null;
    }

    const generation = ++loadGenerationRef.current;
    const isCurrent = () => generation === loadGenerationRef.current;
    try {
      setIsLoading(true);
      if (!unlessEdited) setIsReplacingRows(true);
      setError(null);

      const response = await getSurveillance(dateToLoad);
      // Superseded (another date was selected, or a newer load started): this
      // response must not replace the rows or the change tracking.
      if (!isCurrent()) return null;
      if (unlessEdited && hasUnsavedChangesRef.current) {
        setMessage({
          text: `A run for ${dateToLoad} finished. Save or discard your edits, then click Refresh to see its results.`,
          severity: 'warning',
        });
        return null;
      }

      const formattedData = response.data.map(item => {
        const formattedItem = {};
        Object.keys(item).forEach(key => {
          // Convert field names to uppercase to match column definitions
          const upperKey = key.toUpperCase();
          let value = item[key];

          // Preserve date fields as YYYY-MM-DD strings
          if (upperKey === 'ASOFDT' || upperKey === 'CDU_DATE' || upperKey === 'SETTLE_DT' || upperKey === 'SETTLE_DATE') {
            if (value === null || value === undefined || value === '') {
              formattedItem[upperKey] = '';
            } else if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(value)) {
              // Already in YYYY-MM-DD format, keep as-is
              formattedItem[upperKey] = value;
            } else if (value instanceof Date) {
              // Convert Date object to YYYY-MM-DD
              const year = value.getUTCFullYear();
              const month = String(value.getUTCMonth() + 1).padStart(2, '0');
              const day = String(value.getUTCDate()).padStart(2, '0');
              formattedItem[upperKey] = `${year}-${month}-${day}`;
            } else if (typeof value === 'string' && value.includes('T')) {
              // ISO string, extract YYYY-MM-DD
              formattedItem[upperKey] = value.split('T')[0];
            } else {
              // Try to parse and format
              try {
                const date = new Date(value);
                if (!isNaN(date.getTime())) {
                  const year = date.getUTCFullYear();
                  const month = String(date.getUTCMonth() + 1).padStart(2, '0');
                  const day = String(date.getUTCDate()).padStart(2, '0');
                  formattedItem[upperKey] = `${year}-${month}-${day}`;
                } else {
                  formattedItem[upperKey] = value || '';
                }
              } catch (error) {
                formattedItem[upperKey] = value || '';
              }
            }
          } else if (upperKey === 'MODIFIED_DATE') {
            // The writer's concurrency token: kept exactly as loaded (never
            // blanked, parsed or reformatted), null for a never-saved row.
            formattedItem[upperKey] = value;
          } else {
            formattedItem[upperKey] = value === null ? '' : value;
          }
        });
        return formattedItem;
      });

      // The freshly loaded rows: the comparison base, and the MODIFIED_DATE
      // every save sends.
      const originalDataMap = new Map();
      formattedData.forEach(row => {
        originalDataMap.set(row.SURVEILLANCE_ID, { ...row });
      });
      setOriginalRowData(originalDataMap);

      // Fresh data resets change tracking, except edits a failed save hands back.
      const reapplied = reapplyPendingEdits(formattedData, pendingEdits);
      setRowData(reapplied.rows);
      setModifiedRows(new Set(reapplied.changedCells.keys()));
      setChangedCells(reapplied.changedCells);
      setHasUnsavedChanges(reapplied.changedCells.size > 0);
      setLoadedDate(dateToLoad);
      return reapplied;
    } catch (error) {
      if (!isCurrent()) return null;
      console.error("Error fetching surveillance data:", error);
      if (isPermissionDenied(error)) {
        // Permission denial: surface the guidance and do NOT render fake
        // sample data (which would silently mislead the user into thinking
        // they're looking at real positions).
        setError(getPermissionDeniedMessage(error));
      } else {
        setError("Failed to load surveillance data. Please try again.");
      }
      return null;
    } finally {
      if (isCurrent()) {
        setIsLoading(false);
        setIsReplacingRows(false);
      }
    }
  }, []);

  // Load surveillance data when selected date changes. Unsaved edits from
  // another date's conflict never carry over to the new dataset. (Dashboard
  // asks before switching dates over unsaved edits.) The previous date's rows
  // and change tracking are cleared at once, so they are never shown, edited,
  // saved or exported under the new date while its rows load.
  useEffect(() => {
    setOrphaned(null);
    setRowData([]);
    setOriginalRowData(new Map());
    setModifiedRows(new Set());
    setChangedCells(new Map());
    hasUnsavedChangesRef.current = false;
    setHasUnsavedChanges(false);
    setLoadedDate(null);
    if (selectedDate) {
      loadSurveillanceData(selectedDate);
    } else {
      // No date: drop any in-flight load for the previous one.
      loadGenerationRef.current += 1;
      setIsLoading(false);
      setIsReplacingRows(false);
    }
  }, [selectedDate, loadSurveillanceData]);

  const handleRefresh = useCallback(() => {
    if (selectedDate) {
      loadSurveillanceData(selectedDate);
    }
  }, [selectedDate, loadSurveillanceData]);

  const handleExportToExcel = useCallback(() => {
    // Export only rows loaded for the date the file is named after.
    if (gridRef.current && rowsMatchSelectedDate) {
      const dateStr = selectedDate.replace(/:/g, '-').split('T')[0];
      const params = {
        fileName: `surveillance_data_${dateStr}.xlsx`,
        sheetName: 'Surveillance Data',
        exportMode: 'xlsx',
        skipColumnHeaders: false,
        skipColumnGroupHeaders: false,
        skipPinnedTop: false,
        skipPinnedBottom: false,
        allColumns: false,
        onlySelected: false,
        columnKeys: [
          'SURVEILLANCE_ID', 'CUSIP', 'SECURITY', 'NAIC_DESIGNATIONS', 'BROAD_ASSET_CLASS', 'FINAL_GROUPING',
          'BOOKVALUE', 'GAINLOSS', 'CARRYVALUE', 'MARKETPRICE', 'WAL_F', 'YLD_F', 'SPREAD',
          'WARF', 'CE', 'THICK', 'CPR1M', 'CPR3M', 'CPR6M', 'CPR12M', 'CDR1M', 'CDR3M',
          'CDR6M', 'CDR12M', 'SEV1M', 'SEV3M', 'SEV6M', 'SEV12M', 'DQ60P', 'SPECSERV',
          'DEBTSERV', 'NOIDY', 'UTILIZATION', 'MVOC', 'CCC', 'CDO_WARF', 'LTV', 'SSSGT',
          'LEVERAGE', 'OCCUPANCY', 'SETTLE_DT', 'BOOKPRICE', 'AAA_SPREAD', 'CALLABLE',
          'CDO_MP', 'GROUP_W_RATING', 'RISK_RATING', 'CLASS_RATING', 'ASOFDT', 'LOADID', 'INTEXSTATUS', 'DQ30P', 'WAC',
          'SETTLE_DATE', 'COLLAT_MANAGER', 'SPONSOR_MANAGER', 'RTG_SP', 'RTG_MOODY', 'RTG_FITCH', 'RTG_KBRA',
          'RTG_DBRS', 'ISSUER_TRUST', 'PROSPERITY_ISSUER_CONCENTRATION', 'ACCUMLOSS', 'RTG_BSCR', 'RTG_EJ', 'CDU_DATE',
          'DEAL_NAME', 'SECURITY_NAME_SOURCE', 'STR_PCT'
        ]
      };

      gridRef.current.api.exportDataAsExcel(params);
    }
  }, [selectedDate, rowsMatchSelectedDate]);


  const handleUploadClick = useCallback(() => {
    fileInputRef.current?.click();
  }, []);

  const handleViewDetails = useCallback((data) => {
    setDetailRowData(data);
    setShowDetailModal(true);
  }, []);

  const handleCloseDetailModal = useCallback(() => {
    setShowDetailModal(false);
    setDetailRowData(null);
  }, []);

  // Shared failure handling for both run requests. 409 / 400 / 503 carry the
  // backend's own text, which is shown as is.
  const handleRunRequestError = useCallback((error, { runType, runDate, fallback }) => {
    console.error(`Error requesting ${runType} run:`, error);
    if (isPermissionDenied(error)) {
      setMessage({ text: getPermissionDeniedText(error), severity: 'warning', denial: error });
      return;
    }
    if (isOutcomeUnknown(error)) {
      setMessage({ text: RUN_OUTCOME_UNKNOWN_MESSAGE, severity: 'error' });
      onRunOutcomeUnknown?.();
      return;
    }
    const status = error?.response?.status;
    const activeLoadId = error?.response?.data?.load_id;
    if (status === 409 && activeLoadId) {
      // A matching run is already queued or running: watch it instead.
      onRunAlreadyActive?.({ load_id: activeLoadId, run_type: runType, run_date: runDate });
    }
    setMessage({
      text: getWriteErrorMessage(error, fallback),
      severity: status === 409 ? 'warning' : 'error',
    });
  }, [onRunAlreadyActive, onRunOutcomeUnknown]);

  const handleRunBookConfirm = useCallback(async (selectedAccounts) => {
    if (!selectedDate) {
      setMessage({
        text: 'Please select a date before running books.',
        severity: 'error'
      });
      return;
    }

    const runDate = selectedDate;
    try {
      setIsRequestingBook(true);
      setMessage(null);

      // 202: queued for the Beacon run job; Dashboard's monitor takes over.
      const response = await runBook(runDate, selectedAccounts);
      onRunRequested?.({
        run_type: 'Book',
        run_date: runDate,
        accounts: selectedAccounts,
        ...(response?.data || {}),
      });
    } catch (error) {
      handleRunRequestError(error, {
        runType: 'Book',
        runDate,
        fallback: 'Failed to request the book run. Please try again.',
      });
    } finally {
      setIsRequestingBook(false);
    }
  }, [selectedDate, onRunRequested, handleRunRequestError]);

  const handleRunSurveillanceClick = useCallback(() => {
    setShowConfirmDialog(true);
  }, []);

  const handleConfirmRun = useCallback(async () => {
    if (!selectedDate) {
      setMessage({
        text: 'Please select a date before running surveillance.',
        severity: 'error'
      });
      return;
    }

    const runDate = selectedDate;
    try {
      setIsRequestingRun(true);
      setMessage(null);

      // 202: queued for the Beacon run job; Dashboard's monitor takes over.
      const response = await runSurveillance(runDate);
      onRunRequested?.({
        run_type: 'Monthly',
        run_date: runDate,
        accounts: [],
        ...(response?.data || {}),
      });
    } catch (error) {
      handleRunRequestError(error, {
        runType: 'Monthly',
        runDate,
        fallback: 'Failed to request the surveillance run. Please try again.',
      });
    } finally {
      setIsRequestingRun(false);
    }
  }, [selectedDate, onRunRequested, handleRunRequestError]);

  // A run finished: reload its date if it is on screen — unless the user has
  // unsaved edits, which a reload would discard.
  const handledCompletedRunRef = useRef(completedRun);
  useEffect(() => {
    if (!completedRun || completedRun === handledCompletedRunRef.current) return;
    handledCompletedRunRef.current = completedRun;
    if (!selectedDate || (completedRun.run_date && completedRun.run_date !== selectedDate)) return;
    if (hasUnsavedChangesRef.current) {
      setMessage({
        text: `A run for ${selectedDate} finished. Save or discard your edits, then click Refresh to see its results.`,
        severity: 'warning',
      });
      return;
    }
    // Rechecked when the rows arrive: an edit made meanwhile must survive.
    loadSurveillanceData(selectedDate, null, { unlessEdited: true });
  }, [completedRun, selectedDate, loadSurveillanceData]);

  const handleCancelRun = useCallback(() => {
    setShowConfirmDialog(false);
  }, []);

  const handleViewHistory = useCallback(async (data) => {
    try {
      setIsLoadingHistory(true);
      setHistoryTitle(`History for ${data.CUSIP} - ${data.SECURITY}`);

      const response = await getSurveillanceRowHistory(data.SURVEILLANCE_ID);

      setHistoryData(response.data || []);
      setShowHistoryModal(true);
    } catch (error) {
      console.error('Error fetching history:', error);
      if (isPermissionDenied(error)) {
        // Row history is an ungated GET, so there is no role to request here —
        // the Message variant keeps the Permissions-app URL instead.
        setMessage({ text: getPermissionDeniedMessage(error), severity: 'warning' });
      } else {
        setMessage({
          text: 'Failed to load history data. Please try again.',
          severity: 'error'
        });
      }
    } finally {
      setIsLoadingHistory(false);
    }
  }, []);

  const handleCloseHistoryModal = useCallback(() => {
    setShowHistoryModal(false);
    setHistoryData([]);
    setHistoryTitle('');
  }, []);

  const columnDefs = useMemo(() => [
    {
      headerName: "Actions",
      headerTooltip: "Available actions for this security",
      field: "actions",
      cellStyle: { textAlign: "center" },
      minWidth: 100,
      pinned: 'left',
      editable: false, // Actions column is not editable
      sortable: false,
      filter: false,
      cellRenderer: (params) => (
        <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center' }}>
          <Tooltip title="View Details">
            <IconButton
              color="primary"
              size="small"
              onClick={() => handleViewDetails(params.data)}
            >
              <ViewIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title="View History">
            <IconButton
              color="secondary"
              size="small"
              onClick={() => handleViewHistory(params.data)}
              disabled={isLoadingHistory}
            >
              <HistoryIcon />
            </IconButton>
          </Tooltip>
        </Box>
      ),
      getQuickFilterText: ''
    },
    // Hidden column for SURVEILLANCE_ID - acts as unique identifier
    {
      headerName: "Surveillance ID",
      field: "SURVEILLANCE_ID",
      hide: true, // Hidden column
      suppressColumnsToolPanel: true, // Don't show in column tool panel
      suppressMenu: true, // Don't show column menu
      sortable: false,
      filter: false,
      editable: false
    },
    // Security Identification
    {
      headerName: "CUSIP",
      headerTooltip: "Committee on Uniform Securities Identification Procedures number",
      field: "CUSIP",
      pinned: 'left',
      minWidth: 110,
      editable: false
    },
    {
      headerName: "Security",
      headerTooltip: "Security description",
      field: "SECURITY",
      pinned: 'left',
      minWidth: 200,
      flex: 1,
    },
    {
      headerName: "NAIC Designations",
      headerTooltip: "NAIC designation category",
      field: "NAIC_DESIGNATIONS",
      minWidth: 120
    },
    {
      headerName: "Broad Asset Class",
      headerTooltip: "Broad asset class categorization",
      field: "BROAD_ASSET_CLASS",
      minWidth: 140
    },
    {
      headerName: "Final Grouping",
      headerTooltip: "Final grouping classification",
      field: "FINAL_GROUPING",
      minWidth: 140
    },

    // Financial Values
    {
      headerName: "Book Value",
      headerTooltip: "Book value of the security",
      field: "BOOKVALUE",
      type: 'numericColumn',
      valueFormatter: params => {
        if (params.value == null) return '';
        return new Intl.NumberFormat('en-US', {
          style: 'currency',
          currency: 'USD',
          minimumFractionDigits: 0,
          maximumFractionDigits: 0
        }).format(params.value);
      },
      valueParser: params => {
        if (params.newValue === '' || params.newValue == null) return null;
        // Remove currency symbols, commas, and whitespace
        const cleaned = params.newValue.toString().replace(/[$,\s]/g, '');
        const parsed = Number(cleaned);
        return isNaN(parsed) ? null : parsed;
      },
      minWidth: 120
    },
    {
      headerName: "Gain/Loss",
      headerTooltip: "Unrealized gain or loss",
      field: "GAINLOSS",
      type: 'numericColumn',
      valueFormatter: params => {
        if (params.value == null) return '';
        return new Intl.NumberFormat('en-US', {
          style: 'currency',
          currency: 'USD',
          minimumFractionDigits: 0,
          maximumFractionDigits: 0
        }).format(params.value);
      },
      valueParser: params => {
        if (params.newValue === '' || params.newValue == null) return null;
        // Remove currency symbols, commas, and whitespace
        const cleaned = params.newValue.toString().replace(/[$,\s]/g, '');
        const parsed = Number(cleaned);
        return isNaN(parsed) ? null : parsed;
      },
      cellStyle: params => {
        if (params.value < 0) {
          return { color: '#c62828' };
        } else if (params.value > 0) {
          return { color: '#2e7d32' };
        }
        return {};
      },
      minWidth: 120
    },
    {
      headerName: "Carry Value",
      headerTooltip: "Carrying value of the security",
      field: "CARRYVALUE",
      type: 'numericColumn',
      valueFormatter: params => {
        if (params.value == null) return '';
        return new Intl.NumberFormat('en-US', {
          style: 'currency',
          currency: 'USD',
          minimumFractionDigits: 0,
          maximumFractionDigits: 0
        }).format(params.value);
      },
      valueParser: params => {
        if (params.newValue === '' || params.newValue == null) return null;
        // Remove currency symbols, commas, and whitespace
        const cleaned = params.newValue.toString().replace(/[$,\s]/g, '');
        const parsed = Number(cleaned);
        return isNaN(parsed) ? null : parsed;
      },
      minWidth: 120
    },
    {
      headerName: "Market Price",
      headerTooltip: "Current market price",
      field: "MARKETPRICE",
      type: 'numericColumn',
      valueFormatter: params => {
        if (params.value == null) return '';
        return new Intl.NumberFormat('en-US', {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2
        }).format(params.value);
      },
      minWidth: 110,
      editable: false
    },
    {
      headerName: "Book Price",
      headerTooltip: "Book price of the security",
      field: "BOOKPRICE",
      type: 'numericColumn',
      valueFormatter: params => {
        if (params.value == null) return '';
        return new Intl.NumberFormat('en-US', {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2
        }).format(params.value);
      },
      valueParser: params => {
        if (params.newValue === '' || params.newValue == null) return null;
        // Remove commas and whitespace, parse as number
        const cleaned = params.newValue.toString().replace(/[,\s]/g, '');
        const parsed = Number(cleaned);
        return isNaN(parsed) ? null : parsed;
      },
      minWidth: 110
    },

    // Risk Metrics
    {
      headerName: "WAL",
      headerTooltip: "Weighted Average Life",
      field: "WAL_F",
      type: 'numericColumn',
      valueFormatter: params => {
        if (params.value == null) return '';
        return new Intl.NumberFormat('en-US', {
          minimumFractionDigits: 1,
          maximumFractionDigits: 1
        }).format(params.value);
      },
      minWidth: 80
    },
    {
      headerName: "Yield",
      headerTooltip: "Yield to maturity",
      field: "YLD_F",
      type: 'numericColumn',
      valueFormatter: params => {
        if (params.value == null) return '';
        return new Intl.NumberFormat('en-US', {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2
        }).format(params.value) + '%';
      },
      valueParser: params => {
        if (params.newValue === '') return null;
        // Remove % sign and save the number as-is (5.25% → 5.25)
        return Number(params.newValue.toString().replace(/%/g, ''));
      },
      minWidth: 90
    },
    {
      headerName: "Spread",
      headerTooltip: "Credit spread (basis points)",
      field: "SPREAD",
      type: 'numericColumn',
      valueFormatter: params => {
        if (params.value == null) return '';
        return `${Number(params.value).toFixed(2)} bps`;
      },
      minWidth: 90
    },
    {
      headerName: "WARF",
      headerTooltip: "Weighted Average Rating Factor",
      field: "WARF",
      type: 'numericColumn',
      minWidth: 80
    },
    {
      headerName: "CE",
      headerTooltip: "Credit Enhancement",
      field: "CE",
      type: 'numericColumn',
      valueFormatter: params => {
        if (params.value == null) return '';
        return new Intl.NumberFormat('en-US', {
          minimumFractionDigits: 1,
          maximumFractionDigits: 1
        }).format(params.value) + '%';
      },
      valueParser: params => {
        if (params.newValue === '') return null;
        return Number(params.newValue.toString().replace(/%/g, ''));
      },
      minWidth: 80
    },
    {
      headerName: "Thickness",
      headerTooltip: "Tranche thickness",
      field: "THICK",
      type: 'numericColumn',
      valueFormatter: params => {
        if (params.value == null) return '';
        return new Intl.NumberFormat('en-US', {
          minimumFractionDigits: 1,
          maximumFractionDigits: 1
        }).format(params.value) + '%';
      },
      valueParser: params => {
        if (params.newValue === '') return null;
        return Number(params.newValue.toString().replace(/%/g, ''));
      },
      minWidth: 100
    },

    // Performance Metrics - CPR (Conditional Prepayment Rate)
    {
      headerName: "CPR 1M",
      headerTooltip: "1 Month Conditional Prepayment Rate",
      field: "CPR1M",
      type: 'numericColumn',
      valueFormatter: params => {
        if (params.value == null) return '';
        return new Intl.NumberFormat('en-US', {
          minimumFractionDigits: 1,
          maximumFractionDigits: 1
        }).format(params.value) + '%';
      },
      valueParser: params => {
        if (params.newValue === '') return null;
        return Number(params.newValue.toString().replace(/%/g, ''));
      },
      minWidth: 90
    },
    {
      headerName: "CPR 3M",
      headerTooltip: "3 Month Conditional Prepayment Rate",
      field: "CPR3M",
      type: 'numericColumn',
      valueFormatter: params => {
        if (params.value == null) return '';
        return new Intl.NumberFormat('en-US', {
          minimumFractionDigits: 1,
          maximumFractionDigits: 1
        }).format(params.value) + '%';
      },
      valueParser: params => {
        if (params.newValue === '') return null;
        return Number(params.newValue.toString().replace(/%/g, ''));
      },
      minWidth: 90
    },
    {
      headerName: "CPR 6M",
      headerTooltip: "6 Month Conditional Prepayment Rate",
      field: "CPR6M",
      type: 'numericColumn',
      valueFormatter: params => {
        if (params.value == null) return '';
        return new Intl.NumberFormat('en-US', {
          minimumFractionDigits: 1,
          maximumFractionDigits: 1
        }).format(params.value) + '%';
      },
      valueParser: params => {
        if (params.newValue === '') return null;
        return Number(params.newValue.toString().replace(/%/g, ''));
      },
      minWidth: 90
    },
    {
      headerName: "CPR 12M",
      headerTooltip: "12 Month Conditional Prepayment Rate",
      field: "CPR12M",
      type: 'numericColumn',
      valueFormatter: params => {
        if (params.value == null) return '';
        return new Intl.NumberFormat('en-US', {
          minimumFractionDigits: 1,
          maximumFractionDigits: 1
        }).format(params.value) + '%';
      },
      valueParser: params => {
        if (params.newValue === '') return null;
        return Number(params.newValue.toString().replace(/%/g, ''));
      },
      minWidth: 95
    },

    // CDR (Conditional Default Rate)
    {
      headerName: "CDR 1M",
      headerTooltip: "1 Month Conditional Default Rate",
      field: "CDR1M",
      type: 'numericColumn',
      valueFormatter: params => {
        if (params.value == null) return '';
        return new Intl.NumberFormat('en-US', {
          minimumFractionDigits: 1,
          maximumFractionDigits: 1
        }).format(params.value) + '%';
      },
      valueParser: params => {
        if (params.newValue === '') return null;
        return Number(params.newValue.toString().replace(/%/g, ''));
      },
      minWidth: 90
    },
    {
      headerName: "CDR 3M",
      headerTooltip: "3 Month Conditional Default Rate",
      field: "CDR3M",
      type: 'numericColumn',
      valueFormatter: params => {
        if (params.value == null) return '';
        return new Intl.NumberFormat('en-US', {
          minimumFractionDigits: 1,
          maximumFractionDigits: 1
        }).format(params.value) + '%';
      },
      valueParser: params => {
        if (params.newValue === '') return null;
        return Number(params.newValue.toString().replace(/%/g, ''));
      },
      minWidth: 90
    },
    {
      headerName: "CDR 6M",
      headerTooltip: "6 Month Conditional Default Rate",
      field: "CDR6M",
      type: 'numericColumn',
      valueFormatter: params => {
        if (params.value == null) return '';
        return new Intl.NumberFormat('en-US', {
          minimumFractionDigits: 1,
          maximumFractionDigits: 1
        }).format(params.value) + '%';
      },
      valueParser: params => {
        if (params.newValue === '') return null;
        return Number(params.newValue.toString().replace(/%/g, ''));
      },
      minWidth: 90
    },
    {
      headerName: "CDR 12M",
      headerTooltip: "12 Month Conditional Default Rate",
      field: "CDR12M",
      type: 'numericColumn',
      valueFormatter: params => {
        if (params.value == null) return '';
        return new Intl.NumberFormat('en-US', {
          minimumFractionDigits: 1,
          maximumFractionDigits: 1
        }).format(params.value) + '%';
      },
      valueParser: params => {
        if (params.newValue === '') return null;
        return Number(params.newValue.toString().replace(/%/g, ''));
      },
      minWidth: 95
    },

    // Severity Rates
    {
      headerName: "SEV 1M",
      headerTooltip: "1 Month Severity Rate",
      field: "SEV1M",
      type: 'numericColumn',
      valueFormatter: params => {
        if (params.value == null) return '';
        return new Intl.NumberFormat('en-US', {
          minimumFractionDigits: 1,
          maximumFractionDigits: 1
        }).format(params.value) + '%';
      },
      valueParser: params => {
        if (params.newValue === '') return null;
        return Number(params.newValue.toString().replace(/%/g, ''));
      },
      minWidth: 90
    },
    {
      headerName: "SEV 3M",
      headerTooltip: "3 Month Severity Rate",
      field: "SEV3M",
      type: 'numericColumn',
      valueFormatter: params => {
        if (params.value == null) return '';
        return new Intl.NumberFormat('en-US', {
          minimumFractionDigits: 1,
          maximumFractionDigits: 1
        }).format(params.value) + '%';
      },
      valueParser: params => {
        if (params.newValue === '') return null;
        return Number(params.newValue.toString().replace(/%/g, ''));
      },
      minWidth: 90
    },
    {
      headerName: "SEV 6M",
      headerTooltip: "6 Month Severity Rate",
      field: "SEV6M",
      type: 'numericColumn',
      valueFormatter: params => {
        if (params.value == null) return '';
        return new Intl.NumberFormat('en-US', {
          minimumFractionDigits: 1,
          maximumFractionDigits: 1
        }).format(params.value) + '%';
      },
      valueParser: params => {
        if (params.newValue === '') return null;
        return Number(params.newValue.toString().replace(/%/g, ''));
      },
      minWidth: 90
    },
    {
      headerName: "SEV 12M",
      headerTooltip: "12 Month Severity Rate",
      field: "SEV12M",
      type: 'numericColumn',
      valueFormatter: params => {
        if (params.value == null) return '';
        return new Intl.NumberFormat('en-US', {
          minimumFractionDigits: 1,
          maximumFractionDigits: 1
        }).format(params.value) + '%';
      },
      valueParser: params => {
        if (params.newValue === '') return null;
        return Number(params.newValue.toString().replace(/%/g, ''));
      },
      minWidth: 95
    },

    // Additional Metrics
    {
      headerName: "DQ30+",
      headerTooltip: "Delinquency 30+ days",
      field: "DQ30P",
      type: 'numericColumn',
      valueFormatter: params => {
        if (params.value == null) return '';
        return new Intl.NumberFormat('en-US', {
          minimumFractionDigits: 1,
          maximumFractionDigits: 1
        }).format(params.value) + '%';
      },
      valueParser: params => {
        if (params.newValue === '') return null;
        return Number(params.newValue.toString().replace(/%/g, ''));
      },
      minWidth: 90
    },
    {
      headerName: "DQ60+",
      headerTooltip: "Delinquency 60+ days",
      field: "DQ60P",
      type: 'numericColumn',
      valueFormatter: params => {
        if (params.value == null) return '';
        return new Intl.NumberFormat('en-US', {
          minimumFractionDigits: 1,
          maximumFractionDigits: 1
        }).format(params.value) + '%';
      },
      valueParser: params => {
        if (params.newValue === '') return null;
        return Number(params.newValue.toString().replace(/%/g, ''));
      },
      minWidth: 90
    },
    {
      headerName: "LTV",
      headerTooltip: "Loan to Value Ratio",
      field: "LTV",
      type: 'numericColumn',
      valueFormatter: params => {
        if (params.value == null) return '';
        return new Intl.NumberFormat('en-US', {
          minimumFractionDigits: 1,
          maximumFractionDigits: 1
        }).format(params.value) + '%';
      },
      valueParser: params => {
        if (params.newValue === '') return null;
        return Number(params.newValue.toString().replace(/%/g, ''));
      },
      minWidth: 80
    },
    {
      headerName: "Leverage",
      headerTooltip: "Leverage ratio",
      field: "LEVERAGE",
      type: 'numericColumn',
      valueFormatter: params => {
        if (params.value == null) return '';
        return new Intl.NumberFormat('en-US', {
          minimumFractionDigits: 1,
          maximumFractionDigits: 1
        }).format(params.value) + 'x';
      },
      minWidth: 95
    },
    {
      headerName: "Occupancy",
      headerTooltip: "Occupancy rate",
      field: "OCCUPANCY",
      type: 'numericColumn',
      valueFormatter: params => {
        if (params.value == null) return '';
        return new Intl.NumberFormat('en-US', {
          minimumFractionDigits: 1,
          maximumFractionDigits: 1
        }).format(params.value) + '%';
      },
      valueParser: params => {
        if (params.newValue === '') return null;
        return Number(params.newValue.toString().replace(/%/g, ''));
      },
      minWidth: 105
    },
    {
      headerName: "WAC",
      headerTooltip: "Weighted Average Coupon",
      field: "WAC",
      type: 'numericColumn',
      valueFormatter: params => {
        if (params.value == null) return '';
        return new Intl.NumberFormat('en-US', {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2
        }).format(params.value) + '%';
      },
      valueParser: params => {
        if (params.newValue === '') return null;
        return Number(params.newValue.toString().replace(/%/g, ''));
      },
      minWidth: 80
    },

    // Ratings
    {
      headerName: "S&P Rating",
      headerTooltip: "Standard & Poor's rating",
      field: "RTG_SP",
      minWidth: 100
    },
    {
      headerName: "Moody's Rating",
      headerTooltip: "Moody's rating",
      field: "RTG_MOODY",
      minWidth: 115
    },
    {
      headerName: "Fitch Rating",
      headerTooltip: "Fitch rating",
      field: "RTG_FITCH",
      minWidth: 105
    },
    {
      headerName: "Group W Rating",
      headerTooltip: "Group weighted rating",
      field: "GROUP_W_RATING",
      minWidth: 125
    },
    {
      headerName: "Risk Rating",
      headerTooltip: "Internal risk rating",
      field: "RISK_RATING",
      type: 'numericColumn',
      valueFormatter: (params) => {
        if (params.value == null || params.value === '') return '';
        const n = Number(params.value);
        if (Number.isNaN(n)) return params.value;
        return new Intl.NumberFormat('en-US', {
          minimumFractionDigits: 0,
          maximumFractionDigits: 2
        }).format(n);
      },
      valueParser: (params) => {
        if (params.newValue === '' || params.newValue == null) return null;
        const cleaned = params.newValue.toString().replace(/[,\s]/g, '');
        const parsed = Number(cleaned);
        return isNaN(parsed) ? null : parsed;
      },
      minWidth: 110
    },
    {
      headerName: "Class Rating",
      headerTooltip: "Internal class rating",
      field: "CLASS_RATING",
      minWidth: 115
    },

    // Dates
    {
      headerName: "Settle Date",
      headerTooltip: "Settlement date",
      field: "SETTLE_DT",
      editable: false,
      // valueFormatter: params => {
      //     if (!params.value) return '';
      //     try {
      //         const date = new Date(params.value);
      //         return date;
      //     } catch (error) {
      //         return params.value;
      //     }
      // },
      minWidth: 110
    },
    {
      headerName: "As Of Date",
      headerTooltip: "As of date for the data",
      field: "ASOFDT",
      valueFormatter: params => {
        if (!params.value) return '';
        // If already in YYYY-MM-DD format, return as-is
        if (typeof params.value === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(params.value)) {
          return params.value;
        }
        // If it's a Date object, extract YYYY-MM-DD
        if (params.value instanceof Date) {
          const year = params.value.getFullYear();
          const month = String(params.value.getMonth() + 1).padStart(2, '0');
          const day = String(params.value.getDate()).padStart(2, '0');
          return `${year}-${month}-${day}`;
        }
        // If it's an ISO string, extract YYYY-MM-DD
        if (typeof params.value === 'string' && params.value.includes('T')) {
          return params.value.split('T')[0];
        }
        // Try to parse and format
        try {
          const date = new Date(params.value);
          if (!isNaN(date.getTime())) {
            const year = date.getUTCFullYear();
            const month = String(date.getUTCMonth() + 1).padStart(2, '0');
            const day = String(date.getUTCDate()).padStart(2, '0');
            return `${year}-${month}-${day}`;
          }
        } catch (error) {
          // Fallback to original value
        }
        return params.value;
      },
      minWidth: 110,
      editable: false
    },
    {
      headerName: "CDU Date",
      headerTooltip: "CDU date",
      field: "CDU_DATE",
      valueFormatter: params => {
        if (!params.value) return '';
        // If already in YYYY-MM-DD format, return as-is
        if (typeof params.value === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(params.value)) {
          return params.value;
        }
        // If it's a Date object, extract YYYY-MM-DD
        if (params.value instanceof Date) {
          const year = params.value.getFullYear();
          const month = String(params.value.getMonth() + 1).padStart(2, '0');
          const day = String(params.value.getDate()).padStart(2, '0');
          return `${year}-${month}-${day}`;
        }
        // If it's an ISO string, extract YYYY-MM-DD
        if (typeof params.value === 'string' && params.value.includes('T')) {
          return params.value.split('T')[0];
        }
        // Try to parse and format
        try {
          const date = new Date(params.value);
          if (!isNaN(date.getTime())) {
            const year = date.getUTCFullYear();
            const month = String(date.getUTCMonth() + 1).padStart(2, '0');
            const day = String(date.getUTCDate()).padStart(2, '0');
            return `${year}-${month}-${day}`;
          }
        } catch (error) {
          // Fallback to original value
        }
        return params.value;
      },
      minWidth: 110,
      editable: false
    },
    {
      headerName: "Deal Name",
      headerTooltip: "Deal name",
      field: "DEAL_NAME",
      minWidth: 150
    },
    {
      headerName: "Security Name Source",
      headerTooltip: "Security name source",
      field: "SECURITY_NAME_SOURCE",
      minWidth: 180
    },

    // Additional Metrics
    {
      headerName: "Special Servicing",
      headerTooltip: "Special servicing percentage",
      field: "SPECSERV",
      type: 'numericColumn',
      valueFormatter: params => {
        if (params.value == null) return '';
        return new Intl.NumberFormat('en-US', {
          minimumFractionDigits: 1,
          maximumFractionDigits: 1
        }).format(params.value) + '%';
      },
      valueParser: params => {
        if (params.newValue === '') return null;
        return Number(params.newValue.toString().replace(/%/g, ''));
      },
      minWidth: 120
    },
    {
      headerName: "Debt Service",
      headerTooltip: "Debt service coverage ratio",
      field: "DEBTSERV",
      type: 'numericColumn',
      valueFormatter: params => {
        if (params.value == null) return '';
        return new Intl.NumberFormat('en-US', {
          minimumFractionDigits: 1,
          maximumFractionDigits: 1
        }).format(params.value);
      },
      minWidth: 110
    },
    {
      headerName: "NOI/DY",
      headerTooltip: "Net Operating Income to Debt Yield",
      field: "NOIDY",
      type: 'numericColumn',
      valueFormatter: params => {
        if (params.value == null) return '';
        return new Intl.NumberFormat('en-US', {
          minimumFractionDigits: 1,
          maximumFractionDigits: 1
        }).format(params.value) + '%';
      },
      valueParser: params => {
        if (params.newValue === '') return null;
        return Number(params.newValue.toString().replace(/%/g, ''));
      },
      minWidth: 90
    },
    {
      headerName: "Utilization",
      headerTooltip: "Utilization rate",
      field: "UTILIZATION",
      type: 'numericColumn',
      valueFormatter: params => {
        if (params.value == null) return '';
        return new Intl.NumberFormat('en-US', {
          minimumFractionDigits: 1,
          maximumFractionDigits: 1
        }).format(params.value) + '%';
      },
      valueParser: params => {
        if (params.newValue === '') return null;
        return Number(params.newValue.toString().replace(/%/g, ''));
      },
      minWidth: 100
    },
    {
      headerName: "MVOC",
      headerTooltip: "Market Value Over Cost",
      field: "MVOC",
      type: 'numericColumn',
      valueFormatter: params => {
        if (params.value == null) return '';
        return new Intl.NumberFormat('en-US', {
          minimumFractionDigits: 1,
          maximumFractionDigits: 1
        }).format(params.value) + '%';
      },
      valueParser: params => {
        if (params.newValue === '') return null;
        return Number(params.newValue.toString().replace(/%/g, ''));
      },
      minWidth: 90
    },
    {
      headerName: "STR Pct",
      headerTooltip: "Short Term Rental percentage",
      field: "STR_PCT",
      type: 'numericColumn',
      valueFormatter: params => {
        if (params.value == null) return '';
        return new Intl.NumberFormat('en-US', {
          minimumFractionDigits: 1,
          maximumFractionDigits: 1
        }).format(params.value) + '%';
      },
      valueParser: params => {
        if (params.newValue === '') return null;
        return Number(params.newValue.toString().replace(/%/g, ''));
      },
      minWidth: 90
    },
    {
      headerName: "CCC",
      headerTooltip: "CCC rating percentage",
      field: "CCC",
      type: 'numericColumn',
      valueFormatter: params => {
        if (params.value == null) return '';
        return new Intl.NumberFormat('en-US', {
          minimumFractionDigits: 1,
          maximumFractionDigits: 1
        }).format(params.value * 100) + '%';
      },
      valueParser: params => {
        if (params.newValue === '') return null;
        // CCC is special: stored as decimal in database (5.5% → 0.055)
        return Number(params.newValue.toString().replace(/%/g, '')) / 100;
      },
      minWidth: 80
    },
    {
      headerName: "CDO WARF",
      headerTooltip: "CDO Weighted Average Rating Factor",
      field: "CDO_WARF",
      type: 'numericColumn',
      minWidth: 100
    },
    {
      headerName: "SSSGT",
      headerTooltip: "Single Select Super Senior GT",
      field: "SSSGT",
      type: 'numericColumn',
      valueFormatter: params => {
        if (params.value == null) return '';
        return new Intl.NumberFormat('en-US', {
          minimumFractionDigits: 1,
          maximumFractionDigits: 1
        }).format(params.value) + '%';
      },
      valueParser: params => {
        if (params.newValue === '') return null;
        return Number(params.newValue.toString().replace(/%/g, ''));
      },
      minWidth: 90
    },
    {
      headerName: "AAA Spread",
      headerTooltip: "AAA spread (basis points)",
      field: "AAA_SPREAD",
      type: 'numericColumn',
      valueFormatter: params => {
        if (params.value == null || params.value === '' || Number(params.value) === 0) return '';
        return `${Number(params.value).toFixed(2)} bps`;
      },
      minWidth: 110
    },
    {
      headerName: "CDO MP",
      headerTooltip: "CDO Market Price",
      field: "CDO_MP",
      type: 'numericColumn',
      valueFormatter: params => {
        if (params.value == null) return '';
        return new Intl.NumberFormat('en-US', {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2
        }).format(params.value);
      },
      minWidth: 90
    },
    {
      headerName: "Accumulated Loss",
      headerTooltip: "Accumulated loss amount",
      field: "ACCUMLOSS",
      type: 'numericColumn',
      valueFormatter: params => {
        if (params.value == null) return '';
        return new Intl.NumberFormat('en-US', {
          style: 'currency',
          currency: 'USD',
          minimumFractionDigits: 0,
          maximumFractionDigits: 0
        }).format(params.value);
      },
      minWidth: 130
    },

    // More Ratings
    {
      headerName: "KBRA Rating",
      headerTooltip: "KBRA rating",
      field: "RTG_KBRA",
      minWidth: 105
    },
    {
      headerName: "DBRS Rating",
      headerTooltip: "DBRS rating",
      field: "RTG_DBRS",
      minWidth: 105
    },
    {
      headerName: "BSCR Rating",
      headerTooltip: "BSCR rating",
      field: "RTG_BSCR",
      minWidth: 105
    },
    {
      headerName: "EJ Rating",
      headerTooltip: "EJ rating",
      field: "RTG_EJ",
      minWidth: 95
    },

    // Alternative Settle Date
    {
      headerName: "Settle Date Alt",
      headerTooltip: "Alternative settlement date format",
      field: "SETTLE_DATE",
      editable: false,
      // valueFormatter: params => {
      //     if (!params.value) return '';
      //     try {
      //         const date = new Date(params.value);
      //         return date;
      //     } catch (error) {
      //         return params.value;
      //     }
      // },
      minWidth: 130
    },

    // Additional Fields
    {
      headerName: "Callable",
      headerTooltip: "Whether the security is callable",
      field: "CALLABLE",
      minWidth: 80
    },
    {
      headerName: "Status",
      headerTooltip: "Intex status",
      field: "INTEXSTATUS",
      minWidth: 90
    },
    {
      headerName: "Load ID",
      headerTooltip: "Data load identifier",
      field: "LOADID",
      minWidth: 90,
      editable: false
    },
    {
      headerName: "Collateral Manager",
      headerTooltip: "Collateral manager name",
      field: "COLLAT_MANAGER",
      minWidth: 150
    },
    {
      headerName: "Sponsor Manager",
      headerTooltip: "Sponsor manager name",
      field: "SPONSOR_MANAGER",
      minWidth: 160
    },
    {
      headerName: "Issuer Trust",
      headerTooltip: "Issuer trust name",
      field: "ISSUER_TRUST",
      minWidth: 150
    },
    {
      headerName: "Issuer Concentration",
      headerTooltip: "Prosperity issuer concentration (from Clearwater)",
      field: "PROSPERITY_ISSUER_CONCENTRATION",
      minWidth: 170
    },
    {
      headerName: "Notes",
      headerTooltip: "Notes",
      field: "NOTES",
      minWidth: 150
    },
    {
      headerName: "Modified By",
      headerTooltip: "Modified by",
      field: "MODIFIED_BY",
      minWidth: 150,
      editable: false
    }, {
      headerName: "Modified Date",
      headerTooltip: "Modified date",
      field: "MODIFIED_DATE",
      editable: false,
      valueFormatter: params => {
        if (!params.value) return '';
        try {
          const date = new Date(params.value);
          return date.toLocaleString();
        } catch (error) {
          return params.value;
        }
      },
      comparator: (valueA, valueB) => {
        if (!valueA && !valueB) return 0;
        if (!valueA) return -1;
        if (!valueB) return 1;

        try {
          const dateA = new Date(valueA).getTime();
          const dateB = new Date(valueB).getTime();

          if (isNaN(dateA) && isNaN(dateB)) return 0;
          if (isNaN(dateA)) return -1;
          if (isNaN(dateB)) return 1;

          return dateA - dateB;
        } catch (error) {
          return 0;
        }
      },
      minWidth: 150,
    },

  ], [handleViewDetails, handleViewHistory, isLoadingHistory]);

  // File upload handler — uses 1823-core parseFileData + mergeRows utilities.
  const handleFileUpload = useCallback(async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    // Reset file input
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }

    if (!rowsMatchSelectedDate || isReplacingRows || rowData.length === 0) {
      setMessage({
        text: 'Please select a date and ensure grid has data before uploading.',
        severity: 'error'
      });
      return;
    }

    try {
      setIsUploading(true);
      setError(null);
      setMessage(null);

      // Parse the file using 1823-core (handles CSV and Excel)
      const rawRows = await parseFileData(file);

      if (rawRows.length === 0) {
        throw new Error('No data rows found in the uploaded file. Please ensure there is at least one data row after the header row.');
      }

      // Normalize row keys using the surveillance column normalizer (display → DB field names)
      const parsedData = rawRows.map(row => normalizeRowKeys(row, surveillanceColumnNormalizer));

      // Validate that CUSIP column is present after normalization
      const hasCusipColumn = parsedData.some(row => 'CUSIP' in row && row['CUSIP'] !== '' && row['CUSIP'] != null);

      if (!hasCusipColumn) {
        const availableColumns = parsedData.length > 0 ? Object.keys(parsedData[0]).join(', ') : 'none';
        throw new Error(`Uploaded file must contain a CUSIP column. Found columns: ${availableColumns}`);
      }

      // Build state accumulators for change tracking (must happen before mergeRows
      // so we can inspect each matched pair against originalRowData).
      const updatedModifiedRows = new Set(modifiedRows);
      const updatedChangedCells = new Map(changedCells);

      // Use mergeRows to upsert uploaded rows into existing grid data.
      // onUpdateRow applies date normalization and records per-field changes.
      const { mergedData, updatedKeys, unchangedCount } = mergeRows({
        uploadedRows: parsedData,
        existingRows: rowData,
        matchField: 'CUSIP',
        mergeStrategy: 'upsert',
        onUpdateRow: (existingRow, uploadedRow) => {
          const merged = { ...existingRow };
          const rowId = existingRow.SURVEILLANCE_ID;
          const originalRow = originalRowData.get(rowId) || existingRow;
          const rowChangedFields = new Set(updatedChangedCells.get(rowId) || []);

          Object.keys(uploadedRow).forEach(field => {
            if (SYSTEM_FIELDS.has(field)) return;

            const uploadedValue = uploadedRow[field];
            const uploadedIsEmpty = uploadedValue === null || uploadedValue === undefined || uploadedValue === '';
            if (uploadedIsEmpty) return;

            // Apply date normalization for date fields
            let processedValue = DATE_FIELDS.has(field) ? normalizeDateValue(uploadedValue) : uploadedValue;
            merged[field] = processedValue;

            // Track changes against original
            const originalValue = originalRow[field];
            const normVal = v => (v === null || v === undefined || v === '') ? null : v;
            const origN = normVal(originalValue);
            const mergedN = normVal(processedValue);

            let hasChange = false;
            if (origN === null && mergedN !== null) {
              hasChange = true;
            } else if (origN !== null && mergedN === null) {
              hasChange = true;
            } else if (origN !== null && mergedN !== null) {
              if (typeof origN === 'number' && typeof mergedN === 'number') {
                const tolerance = (field === 'GAINLOSS' || field === 'BOOKVALUE' || field === 'CARRYVALUE' || field === 'ACCUMLOSS') ? 0.01 : 0.0001;
                hasChange = Math.abs(origN - mergedN) > tolerance;
              } else {
                hasChange = String(origN).trim() !== String(mergedN).trim();
              }
            }

            if (hasChange) rowChangedFields.add(field);
          });

          if (rowChangedFields.size > 0) {
            updatedChangedCells.set(rowId, rowChangedFields);
          }

          // MODIFIED_BY / MODIFIED_DATE stay as loaded: the writer stamps both.
          return merged;
        },
      });

      const matchedCount = updatedKeys.length;
      const unmatchedCount = parsedData.length - matchedCount - unchangedCount;

      // Accumulate modified row IDs
      updatedKeys.forEach(cusip => {
        const matched = mergedData.find(r => r.CUSIP === cusip);
        if (matched) updatedModifiedRows.add(matched.SURVEILLANCE_ID);
      });

      // Update state
      setRowData(mergedData);
      setModifiedRows(updatedModifiedRows);
      setChangedCells(updatedChangedCells);
      if (matchedCount > 0) hasUnsavedChangesRef.current = true;
      setHasUnsavedChanges(matchedCount > 0);

      setMessage({
        text: `Upload completed. ${matchedCount} row(s) updated, ${unmatchedCount} row(s) not found in grid.`,
        severity: 'success'
      });

      // Refresh grid to show changes
      if (gridRef.current?.api) {
        gridRef.current.api.refreshCells({ force: true });
      }
    } catch (error) {
      console.error('Error uploading file:', error);
      setMessage({
        text: error.message || 'Failed to upload file. Please try again.',
        severity: 'error'
      });
    } finally {
      setIsUploading(false);
    }
  }, [rowsMatchSelectedDate, isReplacingRows, rowData, modifiedRows, changedCells, originalRowData]);

  const defaultColDef = useMemo(() => ({
    sortable: true,
    filter: true,
    resizable: true,
    // requires surveillance.results:write, and rows loaded for the selected
    // date (an edit made while a load replaces the rows would be lost)
    editable: canSaveResults && rowsMatchSelectedDate && !isReplacingRows,
    cellStyle: (params) => {
      const baseStyle = {
        color: theme.palette.text.primary,
        backgroundColor: theme.palette.background.paper,
        borderColor: theme.palette.divider
      };

      // Check if this cell has been changed
      // Only highlight editable fields that are actually in the changedCells set
      // Exclude non-data fields like "actions" and fields that are explicitly non-editable
      if (params.data && params.colDef.field &&
        params.colDef.editable !== false &&
        params.colDef.field !== 'actions' &&
        params.colDef.field !== 'SURVEILLANCE_ID') {
        const rowId = params.data.SURVEILLANCE_ID;
        const fieldName = params.colDef.field;
        const rowChangedCells = changedCells.get(rowId);

        if (rowChangedCells && rowChangedCells.has(fieldName)) {
          // Highlight changed cells with a different background color
          return {
            ...baseStyle,
            backgroundColor: 'rgba(255, 193, 7, 0.3)', // Light yellow/amber background for changed cells
          };
        }
      }

      return baseStyle;
    },
    menuTabs: ['filterMenuTab', 'columnsMenuTab', 'generalMenuTab'],
  }), [theme, canSaveResults, rowsMatchSelectedDate, isReplacingRows, changedCells]);

  const getRowStyle = useCallback((params) => {
    const rowId = params.data.SURVEILLANCE_ID;
    if (modifiedRows.has(rowId)) {
      return {
        backgroundColor: 'rgba(255, 165, 0, 0.2)', // Orange highlight for modified rows
        border: '1px solid orange'
      };
    }
    return {};
  }, [modifiedRows]);

  const gridModules = useMemo(() => [
    CellStyleModule,
    ClientSideRowModelModule,
    DateFilterModule,
    ExcelExportModule,
    NumberFilterModule,
    QuickFilterModule,
    TextFilterModule,
  ], []);

  const handleEditFilter = useCallback((e) => {
    setQuickFilterText(e.target.value);
  }, []);

  const handleCellValueChanged = useCallback((event) => {
    // Only allow changes for users holding surveillance.results:write
    if (!canSaveResults) {
      return;
    }
    // Rows for the selected date are not on screen yet (the grid is read-only
    // then); the load that is replacing them would drop this edit anyway.
    if (!rowsMatchSelectedDate || isReplacingRows) {
      return;
    }

    // Skip tracking for non-editable fields and non-data fields (like Actions column)
    if (event.colDef.editable === false ||
      !event.colDef.field ||
      event.colDef.field === 'actions' ||
      SYSTEM_FIELDS.has(event.colDef.field)) {
      return;
    }

    // Check if the value has actually changed
    const oldValue = event.oldValue;
    const newValue = event.newValue;

    // Handle null/undefined/empty comparisons
    const normalizeValue = (value) => {
      if (value === null || value === undefined || value === '') return null;
      return value;
    };

    const normalizedOld = normalizeValue(oldValue);
    const normalizedNewValue = normalizeValue(newValue);

    // Check for actual changes
    let hasActualChange = false;

    if (normalizedOld === null && normalizedNewValue === null) {
      hasActualChange = false; // Both are empty/null
    } else if (normalizedOld === null || normalizedNewValue === null) {
      hasActualChange = true; // One is empty, other has value
    } else if (typeof normalizedOld === 'number' && typeof normalizedNewValue === 'number') {
      // For numeric values, check with small tolerance for floating point precision
      hasActualChange = Math.abs(normalizedOld - normalizedNewValue) > 0.0001;
    } else {
      // For string/other comparisons
      hasActualChange = String(normalizedOld).trim() !== String(normalizedNewValue).trim();
    }

    // Only proceed if there's an actual change
    if (!hasActualChange) {
      return;
    }

    // Update the row data with metadata changes
    const rowId = event.data.SURVEILLANCE_ID;
    const fieldName = event.colDef.field;

    // Compare new value with original value to determine if cell is actually changed
    const originalRow = originalRowData.get(rowId);
    if (!originalRow) {
      // If no original data, can't compare - skip tracking
      return;
    }

    const originalValue = originalRow[fieldName];

    // Normalize values for comparison, handling type mismatches
    const normalizeValueForComparison = (val) => {
      if (val === null || val === undefined || val === '') return null;
      // Convert to number if both are numeric strings
      if (typeof val === 'string' && !isNaN(val) && val.trim() !== '') {
        const numVal = Number(val);
        if (!isNaN(numVal)) return numVal;
      }
      return val;
    };

    const normalizedOriginal = normalizeValueForComparison(originalValue);
    const normalizedNew = normalizeValueForComparison(newValue);

    let cellHasChanged = false;
    if (normalizedOriginal === null && normalizedNew !== null) {
      cellHasChanged = true;
    } else if (normalizedOriginal !== null && normalizedNew === null) {
      cellHasChanged = true;
    } else if (normalizedOriginal !== null && normalizedNew !== null) {
      // Ensure both are same type for comparison
      const origType = typeof normalizedOriginal;
      const newType = typeof normalizedNew;

      if (origType === 'number' && newType === 'number') {
        // For currency/numeric fields, use a more appropriate tolerance
        // For currency values (like GAIN/LOSS), use 0.01 tolerance (1 cent)
        // For other numeric fields, use 0.0001
        const tolerance = (fieldName === 'GAINLOSS' || fieldName === 'BOOKVALUE' || fieldName === 'CARRYVALUE' || fieldName === 'ACCUMLOSS') ? 0.01 : 0.0001;
        cellHasChanged = Math.abs(normalizedOriginal - normalizedNew) > tolerance;
      } else {
        // For non-numeric, compare as strings
        cellHasChanged = String(normalizedOriginal).trim() !== String(normalizedNew).trim();
      }
    }

    // MODIFIED_BY / MODIFIED_DATE are not touched: MODIFIED_DATE is the token
    // the save sends back unchanged, and the writer stamps both.
    const { api } = event;

    // Track this specific cell as changed (or remove if reverted to original)
    // Only track the field that was actually edited, not other fields
    setChangedCells(prev => {
      const newMap = new Map(prev);
      if (!newMap.has(rowId)) {
        newMap.set(rowId, new Set());
      }
      const rowChangedCells = newMap.get(rowId);

      // Only add/remove the specific field that was edited
      if (cellHasChanged) {
        // Only add this specific field - don't add any other fields
        rowChangedCells.add(fieldName);
      } else {
        // Cell was reverted to original value, remove from changed set
        rowChangedCells.delete(fieldName);
        // If no cells are changed for this row, remove the row entry
        if (rowChangedCells.size === 0) {
          newMap.delete(rowId);
        }
      }

      // Update modified rows based on whether there are any changed cells for this row
      const hasAnyChanges = newMap.get(rowId) && newMap.get(rowId).size > 0;
      setModifiedRows(prevRows => {
        if (hasAnyChanges) {
          return new Set([...prevRows, rowId]);
        } else {
          const newSet = new Set(prevRows);
          newSet.delete(rowId);
          return newSet;
        }
      });

      return newMap;
    });

    hasUnsavedChangesRef.current = true;
    setHasUnsavedChanges(true);

    // Refresh the edited cell for cell highlighting (row style depends on modifiedRows state)
    if (cellHasChanged && api?.getRowNode) {
      const rowNode = api.getRowNode(rowId);
      if (rowNode) {
        api.refreshCells({ rowNodes: [rowNode], columns: [fieldName], force: true });
      }
    }
  }, [canSaveResults, rowsMatchSelectedDate, isReplacingRows, originalRowData]);

  // Define numeric fields that should not send empty strings
  const numericFields = useMemo(() => [
    'BOOKVALUE', 'GAINLOSS', 'CARRYVALUE', 'MARKETPRICE', 'WAL_F', 'YLD_F',
    'SPREAD', 'WARF', 'CE', 'THICK', 'CPR1M', 'CPR3M', 'CPR6M', 'CPR12M',
    'CDR1M', 'CDR3M', 'CDR6M', 'CDR12M', 'SEV1M', 'SEV3M', 'SEV6M', 'SEV12M',
    'DQ60P', 'SPECSERV', 'DEBTSERV', 'NOIDY', 'UTILIZATION', 'MVOC', 'CCC',
    'CDO_WARF', 'LTV', 'SSSGT', 'LEVERAGE', 'OCCUPANCY', 'BOOKPRICE',
    'AAA_SPREAD', 'CDO_MP', 'DQ30P', 'WAC', 'ACCUMLOSS', 'STR_PCT', 'RISK_RATING',
    'OC_CUSHION', 'LARGEST_ISSUER_PCT', 'COV_LITE_PCT', 'SENIOR_SECURED_PCT',
    'B3_EXPOSURES_PCT', 'PX_LT_80_PCT'
  ], []);

  // Function to sanitize row data for saving
  const sanitizeRowForSave = useCallback((row) => {
    const sanitizedRow = { ...row };

    // Convert empty strings and "nan" to null for numeric fields
    numericFields.forEach(field => {
      const val = sanitizedRow[field];
      if (val === '' || val === null || val === undefined) {
        sanitizedRow[field] = null;
      } else if (typeof val === 'string' && (val.trim() === '' || val.toLowerCase() === 'nan')) {
        sanitizedRow[field] = null;
      }
    });

    // Ensure date fields are saved as YYYY-MM-DD strings
    const dateFields = ['ASOFDT', 'CDU_DATE', 'SETTLE_DT', 'SETTLE_DATE'];
    dateFields.forEach(field => {
      if (sanitizedRow[field] !== null && sanitizedRow[field] !== undefined && sanitizedRow[field] !== '') {
        const value = sanitizedRow[field];
        // If already in YYYY-MM-DD format, keep as-is
        if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(value)) {
          sanitizedRow[field] = value;
        } else if (value instanceof Date) {
          // Convert Date object to YYYY-MM-DD
          const year = value.getUTCFullYear();
          const month = String(value.getUTCMonth() + 1).padStart(2, '0');
          const day = String(value.getUTCDate()).padStart(2, '0');
          sanitizedRow[field] = `${year}-${month}-${day}`;
        } else if (typeof value === 'string' && value.includes('T')) {
          // ISO string, extract YYYY-MM-DD
          sanitizedRow[field] = value.split('T')[0];
        } else {
          // Try to parse and format
          try {
            const date = new Date(value);
            if (!isNaN(date.getTime())) {
              const year = date.getUTCFullYear();
              const month = String(date.getUTCMonth() + 1).padStart(2, '0');
              const day = String(date.getUTCDate()).padStart(2, '0');
              sanitizedRow[field] = `${year}-${month}-${day}`;
            }
          } catch (error) {
            // Keep original value if parsing fails
            console.warn(`Failed to format date field ${field}:`, value);
          }
        }
      } else {
        // Empty/null values should be null
        sanitizedRow[field] = null;
      }
    });

    return sanitizedRow;
  }, [numericFields]);

  const handleSave = useCallback(async () => {
    if (!hasUnsavedChanges || modifiedRows.size === 0) {
      return;
    }
    // Only rows loaded for the date on screen may be written, and never while
    // a load is replacing them.
    if (!rowsMatchSelectedDate || isReplacingRows) {
      return;
    }
    const saveDate = selectedDate;
    // The user may switch dates while the save is in flight; the reload below
    // must then not bring this date's rows back over the new date.
    const stillOnSaveDate = () => selectedDateRef.current === saveDate;
    const modifiedRowsData = [];
    try {
      setIsSaving(true);
      setMessage(null);

      // Get all modified row data
      if (gridRef.current?.api) {
        gridRef.current.api.forEachNode((node) => {
          const rowId = node.data.SURVEILLANCE_ID;
          if (modifiedRows.has(rowId)) {
            modifiedRowsData.push(node.data);
          }
        });
      }

      // Sanitize, then send each row with the MODIFIED_DATE it was LOADED with
      // (the writer's token), unchanged, and without MODIFIED_BY.
      const sentRows = modifiedRowsData.map(row => withLoadedModifiedDate(
        sanitizeRowForSave(row),
        originalRowData.get(row.SURVEILLANCE_ID) || row,
      ));

      const response = await saveSurveillance(sentRows);

      // Adopt the writer's new token at once, so a save made before (or
      // without) the reload below does not 409 against our own write.
      const newModifiedDate = response?.data?.modified_date || null;
      if (newModifiedDate && stillOnSaveDate()) {
        const savedIds = new Set(sentRows.map(row => row.SURVEILLANCE_ID));
        const adopt = row => (savedIds.has(row.SURVEILLANCE_ID) ? { ...row, MODIFIED_DATE: newModifiedDate } : row);
        setRowData(prev => prev.map(adopt));
        setOriginalRowData(prev => {
          const next = new Map(prev);
          modifiedRowsData.forEach(row => {
            next.set(row.SURVEILLANCE_ID, { ...row, MODIFIED_DATE: newModifiedDate });
          });
          return next;
        });
      }

      setMessage({
        text: `Successfully saved ${sentRows.length} modified records!`,
        severity: 'success'
      });

      // Reset change tracking and reload — only while this date is still on
      // screen; otherwise the date switch already reset it, and any tracking
      // now belongs to edits made on the new date.
      if (saveDate && stillOnSaveDate()) {
        setModifiedRows(new Set());
        setChangedCells(new Map());
        setHasUnsavedChanges(false);
        await loadSurveillanceData(saveDate);
      }

    } catch (error) {
      console.error('Error saving surveillance data:', error);
      if (isPermissionDenied(error)) {
        setMessage({ text: getPermissionDeniedText(error), severity: 'warning', denial: error });
      } else if (shouldReloadAfterWriteError(error) && saveDate && stillOnSaveDate()) {
        // 409 (someone else saved first) or an unknown outcome: reload, then
        // put the user's edits back on top so they can review and re-apply
        // them. Never resend automatically with the refreshed MODIFIED_DATE.
        const text = getWriteErrorMessage(error, 'Failed to save changes. Please try again.');
        const pendingEdits = new Map();
        modifiedRowsData.forEach(row => {
          const fields = changedCells.get(row.SURVEILLANCE_ID);
          if (!fields) return;
          const edits = {};
          fields.forEach(field => { edits[field] = row[field]; });
          pendingEdits.set(row.SURVEILLANCE_ID, { row: { ...row }, values: edits });
        });
        const reloaded = await loadSurveillanceData(selectedDate, pendingEdits);
        const orphanCount = reloaded?.orphanedEdits?.length || 0;
        // Add to (never replace) the edits already listed for this date: the
        // panel may be the only copy of them. Only a date switch or Dismiss clears it.
        if (orphanCount > 0) setOrphaned(prev => mergeOrphanedEdits(prev, selectedDate, reloaded.orphanedEdits));
        const orphaned = orphanCount
          ? ` ${orphanCount} edited row(s) are no longer in this load; their edits are listed below and were not saved.`
          : '';
        setMessage({
          text: reloaded
            ? `${text} The grid has been reloaded; changes that are not saved are still highlighted.${orphaned}`
            : `${text} The reload failed; your unsaved edits remain on screen.`,
          severity: isVersionConflict(error) ? 'warning' : 'error'
        });
      } else {
        // 400 / 503 / anything else: the edits stay on screen as they are.
        setMessage({
          text: getWriteErrorMessage(error, 'Failed to save changes. Please try again.'),
          severity: 'error'
        });
      }
    } finally {
      setIsSaving(false);
    }
  }, [hasUnsavedChanges, modifiedRows, changedCells, originalRowData, selectedDate, rowsMatchSelectedDate, isReplacingRows, loadSurveillanceData, sanitizeRowForSave]);

  return (
    <Box sx={{ mt: 4 }}>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-start",
          marginBottom: 2,
        }}
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          <Typography variant="h5">Surveillance Data</Typography>
          {selectedDate && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
              <Typography variant="body2" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <CalendarIcon fontSize="small" color="action" />
                Currently viewing: {selectedDate}
              </Typography>
            </Box>
          )}
        </Box>
        <Box sx={{ display: 'flex', flexDirection: 'row', gap: 1 }}>
          {/* Per-user view-options menu. Permission-agnostic — always visible,
              independent of the run/save capability gates below. */}
          <GridConfigMenu controller={gridConfigs} />
          {canPerformActions ? (
            <Tooltip title={
              !selectedDate ? "Select a date first to run surveillance" :
                !canPerformActions ? "You are not authorized to run surveillance" :
                  isRequestingRun ? "Requesting the surveillance run..." :
                    isRunning ? `A surveillance run for ${selectedDate} is queued or running` :
                      "Run surveillance to generate new data"
            }>
              <span>
                <Button
                  variant="contained"
                  size="medium"
                  startIcon={(isRunning || isRequestingRun) ? <CircularProgress size={20} color="inherit" /> : <PlayIcon />}
                  onClick={handleRunSurveillanceClick}
                  disabled={
                    isRunning ||
                    isRequestingRun ||
                    !selectedDate ||
                    !canPerformActions
                  }
                  sx={{
                    backgroundColor: "lightblue",
                    color: "black",
                    '&:hover': {
                      backgroundColor: 'rgba(173, 216, 230, 0.8)'
                    },
                    '&:disabled': {
                      backgroundColor: 'rgba(173, 216, 230, 0.5)',
                      color: 'rgba(0, 0, 0, 0.6)'
                    }
                  }}
                >
                  {isRequestingRun ? 'Requesting Surveillance...' : isRunning ? 'Running Surveillance...' : 'Run Surveillance'}
                </Button>
              </span>
            </Tooltip>
          ) : (
            <Tooltip title="You are not authorized to run surveillance">
              <span>
                <Button
                  variant="contained"
                  size="medium"
                  disabled
                  sx={{
                    backgroundColor: 'rgba(173, 216, 230, 0.5)',
                    color: 'rgba(0, 0, 0, 0.6)'
                  }}
                >
                  Run Surveillance
                </Button>
              </span>
            </Tooltip>
          )}
          <Tooltip title={
            !selectedDate ? "Select a date first to run books" :
              !canPerformActions ? "You are not authorized to run books" :
                isRequestingBook ? "Requesting the book run..." :
                  isBookRunning ? `A book run for ${selectedDate} is queued or running` :
                    "Run books with selected accounts"
          }>
            <span>
              <Button
                sx={{
                  backgroundColor: "lightcyan",
                  color: "black",
                  '&:hover': {
                    backgroundColor: 'rgba(224, 255, 255, 0.8)'
                  },
                  '&:disabled': {
                    backgroundColor: 'rgba(224, 255, 255, 0.5)',
                    color: 'rgba(0, 0, 0, 0.6)'
                  }
                }}
                startIcon={(isBookRunning || isRequestingBook) ? <CircularProgress size={20} color="inherit" /> : <MenuBookIcon />}
                onClick={() => setShowRunBookModal(true)}
                disabled={
                  isBookRunning ||
                  isRequestingBook ||
                  !selectedDate ||
                  !canPerformActions
                }
              >
                {isRequestingBook ? 'Requesting Books...' : isBookRunning ? 'Running Books...' : 'Run Books'}
              </Button>
            </span>
          </Tooltip>
          <Tooltip title="Refresh surveillance data">
            <span>
              <Button
                sx={{
                  backgroundColor: "lightblue",
                  color: "black",
                  '&:hover': {
                    backgroundColor: 'rgba(173, 216, 230, 0.8)'
                  }
                }}
                startIcon={<RefreshIcon />}
                onClick={handleRefresh}
                disabled={isLoading}
              >
                Refresh
              </Button>
            </span>
          </Tooltip>
          <Tooltip title="Export surveillance data to Excel file">
            <span>
              <Button
                sx={{
                  backgroundColor: "lightgreen",
                  color: "black",
                  '&:hover': {
                    backgroundColor: 'rgba(144, 238, 144, 0.8)'
                  }
                }}
                onClick={handleExportToExcel}
                startIcon={<DownloadIcon />}
                disabled={!rowsMatchSelectedDate || rowData.length === 0}
              >
                Export Excel
              </Button>
            </span>
          </Tooltip>
          <Tooltip title="Upload CSV or Excel file to update grid data (matched by CUSIP)">
            <span>
              <Button
                sx={{
                  backgroundColor: "lightblue",
                  color: "black",
                  '&:hover': {
                    backgroundColor: 'rgba(173, 216, 230, 0.8)'
                  },
                  '&:disabled': {
                    backgroundColor: 'rgba(173, 216, 230, 0.5)',
                    color: 'rgba(0, 0, 0, 0.6)'
                  }
                }}
                onClick={handleUploadClick}
                startIcon={isUploading ? <CircularProgress size={20} color="inherit" /> : <UploadIcon />}
                disabled={!rowsMatchSelectedDate || isReplacingRows || rowData.length === 0 || isUploading}
              >
                {isUploading ? 'Uploading...' : 'Upload'}
              </Button>
            </span>
          </Tooltip>
          <input
            ref={fileInputRef}
            type="file"
            accept=".csv,.xlsx,.xls"
            style={{ display: 'none' }}
            onChange={handleFileUpload}
          />
          {canSaveResults && (
            <Tooltip title={hasUnsavedChanges ? `Save ${modifiedRows.size} modified records` : "No changes to save"}>
              <span>
                <Button
                  sx={{
                    backgroundColor: hasUnsavedChanges ? "orange" : "grey",
                    color: "white",
                    '&:hover': {
                      backgroundColor: hasUnsavedChanges ? 'rgba(255, 165, 0, 0.8)' : 'rgba(128, 128, 128, 0.8)'
                    },
                    '&:disabled': {
                      backgroundColor: 'rgba(128, 128, 128, 0.5)',
                      color: 'rgba(255, 255, 255, 0.6)'
                    }
                  }}
                  onClick={handleSave}
                  startIcon={isSaving ? <CircularProgress size={20} color="inherit" /> : <SaveIcon />}
                  disabled={!hasUnsavedChanges || isSaving || isReplacingRows || !rowsMatchSelectedDate}
                >
                  {isSaving ? 'Saving...' : `Save ${modifiedRows.size > 0 ? `(${modifiedRows.size})` : ''}`}
                </Button>
              </span>
            </Tooltip>
          )}
        </Box>
      </Box>

      {error && (
        <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography color="error">
            {error}
          </Typography>
        </Box>
      )}

      {message && (
        <Box sx={{ mb: 2, display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 1 }}>
          <Typography
            color={message.severity === 'success' ? "success.main" : "error.main"}
          >
            {message.text}
          </Typography>
          {/* Run/save denials land here rather than in the load banner above, so
              the affordance has to be here too — getPermissionDeniedText drops
              the Permissions-app URL on the assumption a button replaces it. */}
          {message.denial && <RequestAccessButton error={message.denial} />}
        </Box>
      )}

      {orphanedEdits.length > 0 && (
        <Box data-testid="orphaned-edits" sx={{ mb: 2, p: 1, border: '1px solid', borderColor: 'warning.main', borderRadius: 1 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="body2" color="warning.main">
              Not saved: these edits were made to rows from an earlier load of {selectedDate} that is no longer the current one. They were not applied to the rows shown below; re-apply them only if they still apply.
            </Typography>
            <Button size="small" onClick={() => setOrphaned(null)}>Dismiss</Button>
          </Box>
          {orphanedEdits.map(edit => (
            <Typography key={edit.SURVEILLANCE_ID} variant="body2" sx={{ fontFamily: 'monospace' }}>
              {edit.CUSIP || edit.SURVEILLANCE_ID} (settle {edit.SETTLE_DATE || '?'}, load {edit.LOADID || '?'}): {Object.entries(edit.values).map(([f, v]) => `${f} = ${v === null || v === undefined ? '' : v}`).join(', ')}
            </Typography>
          ))}
        </Box>
      )}

      {isLoading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', mb: 2 }}>
          <CircularProgress size={24} sx={{ mr: 1 }} />
          <Typography variant="body2">Loading surveillance data...</Typography>
        </Box>
      )}

      <Box sx={{ mb: 2 }}>
        <TextField
          fullWidth
          variant="outlined"
          size="small"
          placeholder="Quick Filter..."
          value={quickFilterText}
          onChange={handleEditFilter}
          InputProps={{
            startAdornment: <SearchIcon color="action" sx={{ mr: 1 }} />,
            sx: {
              backgroundColor: theme.palette.background.paper,
              '& .MuiOutlinedInput-root': {
                '& fieldset': {
                  borderColor: theme.palette.divider,
                },
                '&:hover fieldset': {
                  borderColor: theme.palette.primary.main,
                },
              },
            }
          }}
        />
      </Box>

      <PamGrid
        ref={gridRef}
        rowData={rowData}
        columnDefs={columnDefs}
        defaultColDef={defaultColDef}
        getRowId={getSurveillanceRowId}
        modules={gridModules}
        height="400px"
        // This component renders its own header/toolbar, quick filter, export
        // button, footer note, and loading spinner — keep PamGrid's built-in
        // chrome off so behavior is unchanged.
        showToolbar={false}
        showQuickFilter={false}
        showExport={false}
        showFooter={false}
        quickFilterText={quickFilterText}
        onCellValueChanged={handleCellValueChanged}
        // Auto-apply default layout once the grid API is live.
        onGridReady={gridConfigs.onGridReady}
        // Mirror filter changes into the local stash; column gestures ride
        // through agGridProps as stashBindings.
        onFilterChanged={gridConfigs.onGridStateChanged}
        // Everything the raw grid passed that isn't a PamGrid named prop is
        // preserved verbatim via agGridProps (overrides PamGrid defaults).
        // getRowStyle stays here so the amber/orange change highlight renders.
        agGridProps={{
          rowHeight: 32,
          suppressHorizontalScroll: false,
          undoRedoCellEditing: true,
          enableCellTextSelection: true,
          getRowStyle,
          ...gridConfigs.stashBindings,
        }}
      />

      <Box sx={{ mt: 1, p: 1, backgroundColor: theme.palette.grey[100], borderRadius: 1 }}>
        <Typography variant="body2" color="text.secondary">
          <strong>Note:</strong> This grid displays securities surveillance data including pricing, risk metrics, and performance indicators.
          Select a date above to view surveillance data for that period. Use the filters to search for specific securities or export data for further analysis.
          {canSaveResults ? (
            <span> Most fields are editable - simply double-click a cell to edit. Changes are tracked automatically.</span>
          ) : (
            <span> This data is read-only for your user account.</span>
          )}
          {selectedDate && (
            <span style={{ display: 'block', marginTop: '4px' }}>
              <strong>Currently viewing:</strong> {selectedDate}
            </span>
          )}
          {hasUnsavedChanges && canSaveResults && (
            <span style={{ display: 'block', marginTop: '4px', color: theme.palette.warning.main }}>
              <strong>⚠️ You have unsaved changes</strong>
            </span>
          )}
        </Typography>
      </Box>

      {/* Confirmation Dialog */}
      <ConfirmationDialog
        open={showConfirmDialog}
        onClose={handleCancelRun}
        onConfirm={handleConfirmRun}
        title="Confirm Surveillance Run"
        message={`Are you sure you want to run surveillance for ${selectedDate}? The run is queued for the Beacon run job and may take a while to start and complete; its status is shown in Run History.`}
        confirmText="Run Surveillance"
        confirmColor="primary"
      />

      {/* History Modal */}
      <HistoryModal
        open={showHistoryModal}
        onClose={handleCloseHistoryModal}
        title={historyTitle}
      >
        <SurveillanceHistory
          historyData={historyData}
        />
      </HistoryModal>

      {/* Detail Modal */}
      <SurveillanceDetails
        open={showDetailModal}
        onClose={handleCloseDetailModal}
        rowData={detailRowData}
      />

      {/* Run Book Modal */}
      <RunBookModal
        open={showRunBookModal}
        onClose={() => setShowRunBookModal(false)}
        onConfirm={handleRunBookConfirm}
      />
    </Box>
  );
};

export default SurveillanceGrid; 